import { defineMessages } from 'react-intl';

import Suspense from '@atlaskit/react-ufo/suspense';

import { LoadingSpinner } from '@/components/global-nav/user-profile-new/src/components/spinner';
import { ProductPlan } from '@/components/wpl/pricing/src/price-finder/constants.tsx';
import PriceFinderSection, {
	PriceFinderSectionProps,
} from '@/components/wpl/pricing/src/price-finder/price-finder';
import { PriceFinderContextProvider } from '@/components/wpl/pricing/src/price-finder/price-finder-context.tsx';
import { usePricingPlanComparison } from '@/components/wpl/pricing/src/pricing-content-context';

export interface BitbucketPriceFinderProps {
	serverSideHAMSPricing?: PriceFinderSectionProps['serverSideHAMSPricing'];
	countryCode: PriceFinderSectionProps['countryCode'];
}

export const BitbucketPriceFinder = (props: BitbucketPriceFinderProps) => {
	const content = {
		free: usePricingPlanComparison('Free'),
		standard: usePricingPlanComparison('Standard'),
		premium: usePricingPlanComparison('Premium'),
	};

	const bitbucketProductPlans: ProductPlan[] = [
		{
			planName: content.free?.planTitleExternal ?? 'Free',
			description: content.free?.planOneLiner ?? '',
			recommendationLimit: 0,
			signupLimit: 35000,
			signupURLSuffix: '?bundle=bitbucket&edition=free',
			planFeatureMessages: [],
		},
		{
			planName: content.standard?.planTitleExternal ?? 'Standard',
			description: content.standard?.planOneLiner ?? '',
			recommendationLimit: 5,
			signupLimit: 35000,
			signupURLSuffix: '?bundle=bitbucket&edition=standard',
			planFeatureMessages: [],
		},
		{
			planName: content.premium?.planTitleExternal ?? 'Premium',
			description: content.premium?.planOneLiner ?? '',
			recommendationLimit: 100,
			signupLimit: 35000,
			signupURLSuffix: '?bundle=bitbucket&edition=premium',
			planFeatureMessages: [],
		},
	];
	const messages = defineMessages({
		unitCountInputText: {
			id: 'BitbucketSoftwarePricingPage.priceFinder.unitCountInputText',
			defaultMessage: 'How many users are on your team?',
		},
		unitCountInputTextRedesign: {
			id: 'BitbucketSoftwarePricingPage.priceFinder.unitCountInputTextRedesign',
			defaultMessage: 'Team size:',
		},
		unitCountInputTerm: {
			id: 'BitbucketSoftwarePricingPage.priceFinder.unitCountInputTerm',
			defaultMessage: 'users',
		},
		billingCycleText: {
			id: 'BitbucketSoftwarePricingPage.priceFinder.billingCycleText',
			defaultMessage: 'Billing cycle:',
		},
		unitCountInputTooltip: {
			id: 'BitbucketSoftwarePricingPage.priceFinder.unitCountInputTooltip',
			defaultMessage:
				'A Bitbucket Software user is any user who can log in to Bitbucket Software, and who exists in User Management',
		},
		monthlyExampleHelpText: {
			id: 'BitbucketSoftwarePricingPage.priceFinder.monthlyExampleHelpText',
			defaultMessage:
				'Monthly plans give you flexibility as your number of active users changes, so pay for seats as you use them. Remember that the number of seats may change the total price.',
		},
		annualExampleHelpText: {
			id: 'BitbucketSoftwarePricingPage.priceFinder.annualExampleHelpText',
			defaultMessage:
				'Annual plans give you flexibility and help you prepare for growth. Buy a group of seats at a flat rate now, then fill your empty seats through the year at no extra cost.',
		},
		exampleStandardPlan: {
			id: 'BitbucketSoftwarePricingPage.priceFinder.exampleStandardPlan',
			defaultMessage: 'Pricing for {unitCount} users on Standard plan',
			description: 'Standard is a plan name and should not be translated',
		},
		cardOverSignupLimit: {
			id: 'BitbucketSoftwarePricingPage.priceFinder.cardOverSignupLimit',
			defaultMessage: "Your user count exceeds this plan's limit",
		},
		cardEnterpriseMinimumCount: {
			id: 'BitbucketSoftwarePricingPage.priceFinder.cardEnterpriseMinimumCount',
			defaultMessage:
				'Enterprise plan pricing is available when you enter {enterpriseMinimumCount} or more users above',
			description: 'Enterprise is a plan name and should not be translated',
		},
		cardUnitCountTierRange: {
			id: 'BitbucketSoftwarePricingPage.priceFinder.cardUnitCountTierRange',
			defaultMessage: 'User tier: {lowerLimit} - {upperLimit}',
		},
		cardMonthlyPerUnitCount: {
			id: 'BitbucketSoftwarePricingPageTeam24.priceFinder.cardMonthlyPerUnitCount',
			defaultMessage: 'per user / month',
		},
		cardFreeDescription: {
			id: 'BitbucketSoftwarePricingPage.priceFinder.cardFreeDescription',
			defaultMessage: 'Always free for 10 users',
		},
	});

	return (
		<Suspense interactionName={'bitbucket-price-finder'} fallback={LoadingSpinner}>
			<PriceFinderContextProvider
				dataProductKey="bitbucket"
				productPlans={bitbucketProductPlans}
				defaultUnitCount={5}
				isMonthlyBillingOnly={true}
			>
				<PriceFinderSection
					messages={messages}
					hamsProductKey="com.atlassian.bitbucket.manual"
					{...props}
				/>
			</PriceFinderContextProvider>
		</Suspense>
	);
};
BitbucketPriceFinder.displayName = 'BitbucketPriceFinderSection';
